/**
 * Materials, in the catalog's Materials tab.
 *
 * The biggest list in the catalog by two orders of magnitude — a supplier's
 * price list is four thousand rows — and the one that most needs filters,
 * groups, saved views and import. It gets them by being the same `ListScreen`
 * as every other list in Rowted, in `embedded` chrome so the catalog keeps
 * its own tabs.
 *
 * The form beside it answers three questions in order: what it is, what it
 * costs and how far one of them goes, and what it sells for.
 */
import { useEffect, useState } from 'react'
import type { MeResponse } from '@rowted/shared'
import { ListScreen } from './list-screen.js'
import { MaterialForm } from './forms/material-form.js'
import { Toast } from './overlays.js'
import { useList, type ListRow } from '../lib/use-list.js'
import { EM_DASH, formatNumber } from '../lib/format.js'
import { IconPlus } from '../frame/icons.js'
import { Button } from './primitives.js'
import { KindTile } from '../builder/kind-tile.js'
import {
  ConversionCell, MarginPill, NameWithTag, conversionShort, joined, rateCell,
} from './catalog-cells.js'
import { NEW_BUTTON, STOCKED } from './forms/catalog-copy.js'

export function ItemsList({
  me, canEdit, reloadKey,
}: {
  me: MeResponse
  canEdit: boolean
  reloadKey?: number
}) {
  // The SKU and the category are the primary cell's second line rather than
  // columns of their own, so they are fetched whether or not they are shown.
  // The tag, the second line and the phone card read these whatever is shown.
  const list = useList('items', {
    alwaysFetch: ['stocked', 'categoryName', 'vendorName', 'unit', 'allocation', 'sellPrice',
      'marginPct'],
  })
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  useEffect(() => { if (reloadKey !== undefined) list.reload() }, [reloadKey])

  /*
   * Board 3: the engine's chrome and nothing else. What this list adds is the
   * kind tile, the name with its Stocked tag and its category · vendor line,
   * and the columns — Bought → allocated · Cost · Sells · Margin · On hand.
   */
  return (
    <>
      <ListScreen
        area="items"
        chrome="embedded"
        title="Items"
        list={list}
        actions={canEdit
          ? (
            <Button variant="primary" onClick={() => setEditing('new')}>
              <IconPlus size={15} />{NEW_BUTTON.item}
            </Button>
          )
          : undefined}
        mark={{ width: 44, render: () => <KindTile kind="item" size={28} /> }}
        primaryColumn={{
          field: 'name',
          header: 'Name',
          render: (row: ListRow) => ({
            primary: <NameWithTag name={String(row.name ?? '')} tag={row.stocked ? STOCKED : null} />,
            secondary: joined(row.categoryName, row.vendorName),
          }),
        }}
        cell={{
          allocation: (row) => <ConversionCell text={row.allocation} />,
          unitCost: (row) => rateCell(row.unitCost),
          sellPrice: (row) => rateCell(row.sellPrice),
          purchasePrice: (row) => rateCell(row.purchasePrice),
          marginPct: (row) => <MarginPill pct={row.marginPct} />,
          /* What is on hand, in the unit it is allocated in — `360 sq ft`. */
          onHand: (row) => (Number(row.onHand ?? 0) === 0
            ? <span className="tnum text-text-faint">{EM_DASH}</span>
            : <span className="tnum">{formatNumber(Number(row.onHand))} {String(row.unit ?? '')}</span>),
        }}
        cardLine={(row) => ({
          facts: [conversionShort(row.allocation), row.vendorName as string | null]
            .filter((one): one is string => Boolean(one)),
          trailing: (
            <span className="flex flex-col items-end leading-tight">
              <span className="tnum font-bold text-text">{rateCell(row.sellPrice)}</span>
              {row.marginPct !== null && row.marginPct !== undefined && (
                <span className="tnum text-[11.5px] text-accent">{formatNumber(Number(row.marginPct))}%</span>
              )}
            </span>
          ),
        })}
        rowMenu={(row) => [{ key: 'open', label: 'Open', onSelect: () => setEditing(String(row.id)) }]}
        onRowClick={(row) => setEditing(String(row.id))}
        emptyTitle="No materials yet"
        emptyBody="A material is anything you buy and put on a job. Add one, or import the price list your supplier already sends you."
        emptyAction={canEdit
          ? { label: NEW_BUTTON.item, onClick: () => setEditing('new') }
          : undefined}
        importOrgUnitId={me.scope.selected[0]}
      />

      {editing && (
        <MaterialForm
          itemId={editing === 'new' ? null : editing}
          canEdit={canEdit}
          available={me.scope.available}
          onClose={() => setEditing(null)}
          onSaved={({ message, again }) => {
            /*
             * "Save & add another" leaves the sheet open with the next blank
             * one in it, so the list refreshes underneath and the toast says
             * what landed — but nothing closes.
             */
            if (!again) setEditing(null)
            setNotice(message)
            list.reload()
          }}
        />
      )}

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2">
          <Toast onDismiss={() => setNotice(null)}>{notice}</Toast>
        </div>
      )}
    </>
  )
}

